import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import { InputText, ImageUpload, InputNumber } from 'components/common/input';
import { Alert, Modal } from 'react-bootstrap';
import useCrud from 'core/hooks/api/useCrud';
import { validateForm } from 'core/utils/validation';
import { useFormHandlers } from 'core/hooks/form/formHandlers';
import { postData } from 'core/services/api';
import VerificationCodeModal from 'components/common/auth/VerificationCodeModal';

const INITIAL_VERIFICATION_TIME = 5 * 60;

const Information = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createInformation,
        postError: creatingError,
        postLoading: creating,
        putData: updateInformation,
        putError: updatingError,
        putLoading: updating,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.STORE_INFORMATION + '/1');

    const [formValues, setFormValues] = useState({
        facebook: '',
        instagram: '',
        twitter: '',
        whatsapp: '',
        phone_number: '',
        address: '',
        logo: '',
        ruc: ''
    });
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    // Estados para la sección de datos del usuario
    const [userData, setUserData] = useState({
        usuario: '',
        codigo: '',
        email: '',
        backup_email: '',
        password: '',
        confirmPassword: ''
    });
    const [showAccountRequestModal, setShowAccountRequestModal] = useState(false);
    const [accountRequest, setAccountRequest] = useState({
        usuario: '',
        email: ''
    });
    const [accountRequestError, setAccountRequestError] = useState('');
    const [accountRequestLoading, setAccountRequestLoading] = useState(false);
    const [showUserVerificationModal, setShowUserVerificationModal] = useState(false);
    const [userCodigo, setUserCodigo] = useState('');
    const [isEditingUserData, setIsEditingUserData] = useState(false);
    const [userErrors, setUserErrors] = useState({});
    const [userSuccessMessage, setUserSuccessMessage] = useState(null);
    const [userLoading, setUserLoading] = useState(false);
    const [userVerificationLoading, setUserVerificationLoading] = useState(false);
    const [userVerificationError, setUserVerificationError] = useState('');
    const [timeLeft, setTimeLeft] = useState(INITIAL_VERIFICATION_TIME);
    const storedUsuario = localStorage.getItem('usuario');

    useEffect(() => {
        if (data) {
            setFormValues(data);
        }
    }, [data]);

    useEffect(() => {
        if (!showUserVerificationModal || timeLeft <= 0) return undefined;

        const timer = window.setInterval(() => {
            setTimeLeft((prevTime) => prevTime - 1);
        }, 1000);

        return () => window.clearInterval(timer);
    }, [showUserVerificationModal, timeLeft]);

    const validationRules = [
        { field: 'facebook', method: ['validUrl'] },
        { field: 'instagram', method: [ 'validUrl'] },
        { field: 'twitter', method: ['validUrl'] },
        { field: 'whatsapp', method: 'required' },
    ];

    const { handleChange, handleFileChangeLocal } = useFormHandlers(setFormValues);

    const handleSave = async () => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(formValues, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (formValues.id) {
                await updateInformation(null, formValues, true);
                setSuccessMessage("Información actualizada con éxito.");
            } else {
                await createInformation(formValues, true);
                setSuccessMessage("Información creada con éxito.");
            }
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    };

    // Funciones para edición de datos del usuario
    const handleEditUserData = () => {
        setShowAccountRequestModal(true);
        setAccountRequest({
            usuario: '',
            email: ''
        });
        setAccountRequestError('');
    };

    const handleRequestVerificationCode = async (e) => {
        e.preventDefault();
        setAccountRequestError('');
        setAccountRequestLoading(true);

        try {
            const response = await postData('/login/send-code', {
                email: accountRequest.email,
                usuario: accountRequest.usuario,
            });

            if (response?.status === 200) {
                setShowAccountRequestModal(false);
                setShowUserVerificationModal(true);
                setUserCodigo('');
                setUserVerificationError('');
                setTimeLeft(INITIAL_VERIFICATION_TIME);
                return;
            }

            setAccountRequestError('No se pudo enviar el código. Intente nuevamente.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'No se pudo enviar el código.';
            setAccountRequestError(message);
        } finally {
            setAccountRequestLoading(false);
        }
    };

    const handleVerifyUserCode = async (e) => {
        e.preventDefault();
        setUserVerificationError('');
        setUserVerificationLoading(true);

        try {
            const response = await postData('/login/verify-code', {
                usuario: accountRequest.usuario,
                codigo: userCodigo,
            });

            if (response?.status === 200) {
                setIsEditingUserData(true);
                setShowUserVerificationModal(false);
                setUserVerificationError('');
                setUserData({
                    usuario: accountRequest.usuario,
                    email: '',
                    backup_email: '',
                    password: '',
                    confirmPassword: ''
                });
                return;
            }

            setUserVerificationError('El código ingresado es incorrecto o ha expirado.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'No se pudo validar el código.';
            setUserVerificationError(message);
        } finally {
            setUserVerificationLoading(false);
        }
    };

    const handleSaveUserData = async () => {
        setUserErrors({});
        setUserSuccessMessage(null);

        // Validaciones locales
        if (!userData.usuario || !userData.email) {
            setUserErrors({
                ...(!userData.usuario && { usuario: 'El usuario es requerido' }),
                ...(!userData.email && { email: 'El email es requerido' })
            });
            return;
        }

        // Si llena una contraseña, debe llenar ambas
        if ((userData.password || userData.confirmPassword) && userData.password !== userData.confirmPassword) {
            setUserErrors({ confirmPassword: 'Las contraseñas no coinciden.' });
            return;
        }

        setUserLoading(true);

        try {
            const payload = {
                usuario: userData.usuario,
                email: userData.email,
                codigo: userCodigo
            };

            if (userData.password) {
                payload.password = userData.password;
            }

            if (userData.backup_email && userData.backup_email.trim()) {
                payload.backup_email = userData.backup_email.trim();
            }

            const response = await postData('/login/update', payload);

            if (response?.status === 200) {
                setUserSuccessMessage("Datos actualizados con éxito.");
                setIsEditingUserData(false);
                setUserData({
                    usuario: userData.usuario,
                    email: userData.email,
                    backup_email: userData.backup_email,
                    password: '',
                    confirmPassword: ''
                });
                return;
            }

            setUserErrors({ general: 'No se pudo actualizar los datos. Intente nuevamente.' });
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'Error al actualizar';
            setUserErrors({ general: message });
        } finally {
            setUserLoading(false);
        }
    };

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Información</h5>
                        <button className="btn btn-primary" onClick={handleSave} disabled={updating || creating}>
                            <i className="fa fa-save me-2" aria-hidden="true" />
                            {updating || creating ? "Guardando..." : "Guardar Información"}
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <div className="row">
                <div className="col-md-6 form-group">
                    <InputText
                        label="Facebook"
                        value={formValues.facebook}
                        onChange={(e) => handleChange('facebook', e.target.value)}
                        placeholder="Ingrese el enlace de Facebook"
                        error={errors.facebook}
                    />
                </div>
                <div className="col-md-6 form-group">
                    <InputText
                        label="Instagram"
                        value={formValues.instagram}
                        onChange={(e) => handleChange('instagram', e.target.value)}
                        placeholder="Ingrese el enlace de Instagram"
                        error={errors.instagram}
                    />
                </div>
                <div className="col-md-6 form-group">
                    <InputText
                        label="Tiktok"
                        value={formValues.twitter}
                        onChange={(e) => handleChange('twitter', e.target.value)}
                        placeholder="Ingrese el enlace de Tiktok"
                        error={errors.twitter}
                    />
                </div>
                <div className="col-md-6 form-group">
                    <InputNumber
                        label="WhatsApp"
                        value={formValues.whatsapp}
                        onChange={(e) => handleChange('whatsapp', e.value)}
                        placeholder="Ingrese el número de WhatsApp"
                        error={errors.whatsapp}
                    />
                </div>
                <div className="col-md-6 form-group">
                    <InputText
                        label="Información de contacto"
                        value={formValues.phone_number}
                        onChange={(e) => handleChange('phone_number',  e.target.value)}
                        placeholder="Ingrese la información de contacto"
                        error={errors.phone_number}
                    />
                </div>
                <div className="col-md-6 form-group">
                    <InputText
                        label="Dirección"
                        value={formValues.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        placeholder="Ingrese la dirección"
                        error={errors.address}
                    />
                </div>
                <div className="col-md-6 form-group">
                    <InputText
                        label="RUC"
                        value={formValues.ruc}
                        onChange={(e) => handleChange('ruc', e.target.value)}
                        placeholder="Ingrese el RUC"
                        error={errors.ruc}
                    />
                </div>
                {creatingError || updatingError ? (
                    <div className="col-12 form-group mt-3">
                        <Alert variant="danger">
                            {creatingError?.response?.data?.message || updatingError?.response?.data?.message}
                        </Alert>
                    </div>
                ) : null}
            </div>

            {/* Sección de Datos del Usuario */}
            <div className="row w-100 mb-2 mt-4">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Datos de la Cuenta</h5>
                        {!isEditingUserData && (
                            <button 
                                className="btn btn-primary" 
                                onClick={handleEditUserData}
                            >
                                <i className="fa fa-edit me-2" aria-hidden="true" />
                                Editar
                            </button>
                        )}
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>

            {userSuccessMessage && <Alert variant="success">{userSuccessMessage}</Alert>}
            {userErrors.general && <Alert variant="danger">{userErrors.general}</Alert>}

            <div className="row">
                <div className="col-md-6 form-group">
                    <label>Usuario</label>
                    <input
                        type={isEditingUserData ? "text" : "password"}
                        className={`form-control ${userErrors.usuario ? 'is-invalid' : ''}`}
                        value={isEditingUserData ? userData.usuario : '••••••••'}
                        onChange={(e) => isEditingUserData && setUserData({...userData, usuario: e.target.value})}
                        disabled={!isEditingUserData}
                        placeholder="Usuario"
                    />
                    {userErrors.usuario && <div className="invalid-feedback">{userErrors.usuario}</div>}
                </div>

                <div className="col-md-6 form-group">
                    <label>Email</label>
                    <input
                        type={isEditingUserData ? "email" : "password"}
                        className={`form-control ${userErrors.email ? 'is-invalid' : ''}`}
                        value={isEditingUserData ? userData.email : '••••••••'}
                        onChange={(e) => isEditingUserData && setUserData({...userData, email: e.target.value})}
                        disabled={!isEditingUserData}
                        placeholder="Email"
                    />
                    {userErrors.email && <div className="invalid-feedback">{userErrors.email}</div>}
                </div>

                {isEditingUserData && (
                    <>
                        <div className="col-md-6 form-group">
                            <label>Email de respaldo (opcional)</label>
                            <input
                                type="email"
                                className={`form-control ${userErrors.backup_email ? 'is-invalid' : ''}`}
                                value={userData.backup_email}
                                onChange={(e) => setUserData({...userData, backup_email: e.target.value})}
                                placeholder="Ingrese un email de respaldo"
                            />
                            {userErrors.backup_email && <div className="invalid-feedback">{userErrors.backup_email}</div>}
                        </div>

                        <div className="col-md-6 form-group">
                            <label>Nueva Contraseña (opcional)</label>
                            <input
                                type="password"
                                className={`form-control ${userErrors.password ? 'is-invalid' : ''}`}
                                value={userData.password}
                                onChange={(e) => setUserData({...userData, password: e.target.value})}
                                placeholder="Ingrese la nueva contraseña"
                            />
                            {userErrors.password && <div className="invalid-feedback">{userErrors.password}</div>}
                        </div>

                        <div className="col-md-6 form-group">
                            <label>Confirmar Contraseña</label>
                            <input
                                type="password"
                                className={`form-control ${userErrors.confirmPassword ? 'is-invalid' : userErrors.passwordMatch ? 'is-invalid' : (userData.password && userData.confirmPassword === userData.password ? 'is-valid' : '')}`}
                                value={userData.confirmPassword}
                                onChange={(e) => setUserData({...userData, confirmPassword: e.target.value})}
                                placeholder="Confirme la contraseña"
                            />
                            {userErrors.confirmPassword && <div className="invalid-feedback">{userErrors.confirmPassword}</div>}
                        </div>

                        <div className="col-12 form-group mt-3">
                            <button 
                                className="btn btn-success" 
                                onClick={handleSaveUserData} 
                                disabled={userLoading}
                            >
                                <i className="fa fa-save me-2" aria-hidden="true" />
                                {userLoading ? "Guardando..." : "Guardar Cambios"}
                            </button>
                            <button 
                                className="btn btn-secondary ms-2" 
                                onClick={() => {
                                    setIsEditingUserData(false);
                                    setUserData({usuario: '', email: '', password: '', confirmPassword: ''});
                                    setUserErrors({});
                                }}
                            >
                                Cancelar
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Modal para solicitar el código de verificación */}
            <Modal show={showAccountRequestModal} onHide={() => setShowAccountRequestModal(false)} centered>
                <Modal.Header className="p-3 pb-0" closeButton>
                    <Modal.Title>Recuperar acceso a la cuenta</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">Ingresa tu usuario y correo para recibir un código de verificación.</p>
                    {accountRequestError && <Alert variant="danger">{accountRequestError}</Alert>}
                    <form onSubmit={handleRequestVerificationCode}>
                        <div className="form-group">
                            <label>Usuario</label>
                            <input
                                type="text"
                                className="form-control"
                                value={accountRequest.usuario}
                                onChange={(e) => setAccountRequest({ ...accountRequest, usuario: e.target.value })}
                                placeholder="Ingrese su usuario"
                                required
                            />
                        </div>
                        <div className="form-group mt-3">
                            <label>Correo</label>
                            <input
                                type="email"
                                className="form-control"
                                value={accountRequest.email}
                                onChange={(e) => setAccountRequest({ ...accountRequest, email: e.target.value })}
                                placeholder="Ingrese su correo"
                                required
                            />
                        </div>
                        <div className="mt-3 d-flex justify-content-end">
                            <button type="button" className="btn btn-secondary me-2" onClick={() => setShowAccountRequestModal(false)}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={accountRequestLoading}>
                                {accountRequestLoading ? 'Enviando...' : 'Enviar código'}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* Modal de Verificación de Código */}
            <VerificationCodeModal
                show={showUserVerificationModal}
                onHide={() => {
                    setShowUserVerificationModal(false);
                    setUserCodigo('');
                    setUserVerificationError('');
                    setTimeLeft(INITIAL_VERIFICATION_TIME);
                }}
                onSubmit={handleVerifyUserCode}
                loading={userVerificationLoading}
                error={userVerificationError}
                code={userCodigo}
                onCodeChange={setUserCodigo}
                timeLeft={timeLeft}
            />
        </div>
    );
};

export default React.memo(Information);